/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved. Unauthorized copying, distribution, or use is prohibited.
 */

import { KgDirtree, KgDoc } from './types';

// Pure model for the directory-overview levels (PLAT-1534 giant graphs):
// a level renders the children of `path` as nodes and one merged, weighted
// edge per directory pair. Kept renderer-free so the aggregation rules are
// unit-testable.

// One merged edge per ordered pair: the dominant NON-containment family
// styles it ('contains' as a merged family would make the renderer treat a
// sibling directory as a real container), the summed count sizes it and
// shows in the detail panel.
export const mergeLevelEdges = (
  edges: KgDirtree['edges'],
  path: string,
): NonNullable<KgDoc['links']> => {
  const byPair = new Map<
    string,
    { source: string; target: string; total: number; fams: Map<string, number> }
  >();
  for (const e of edges) {
    if (e.parent !== path) continue;
    const k = `${e.source}\u0000${e.target}`;
    const agg = byPair.get(k) ?? {
      source: e.source,
      target: e.target,
      total: 0,
      fams: new Map<string, number>(),
    };
    agg.total += e.count;
    agg.fams.set(e.family, (agg.fams.get(e.family) ?? 0) + e.count);
    byPair.set(k, agg);
  }
  return [...byPair.values()].map((a, i) => {
    const ranked = [...a.fams.entries()].sort((x, y) => y[1] - x[1]);
    const dominant = ranked.find(([fam]) => fam !== 'contains') ?? ranked[0];
    return {
      id: `agg${i}`,
      source: a.source,
      target: a.target,
      relation: dominant[0] === 'contains' ? 'other' : dominant[0],
      count: a.total,
    };
  });
};

// Synthetic level doc: child directories as nodes (marked _dir so the
// renderer captions them honestly), source_file carrying the human caption
// the detail panel and table already render. A sharded path renders real
// symbols only — synthesizing an (empty) level for it would also poison the
// renderer's layout cache under the shard's own key.
export const buildLevelDoc = (tree: KgDirtree, path: string): KgDoc | null => {
  if (path && tree.dirs.find(d => d.path === path)?.shard) return null;
  const children = tree.dirs.filter(d => d.parent === path);
  return {
    built_at_commit: tree.built_at_commit,
    nodes: children.map(d => ({
      id: d.path,
      label: d.name,
      _dir: true,
      source_file: `${d.symbols.toLocaleString()} symbols · ${d.files.toLocaleString()} files${
        d.shard ? '' : ` · ${d.children} subdirectories`
      }`,
    })),
    links: mergeLevelEdges(tree.edges, path),
  };
};

// The fleet tree (PLAT-1646) stops at repo rows: org -> repo, nothing below. A repo row
// (parent is an org, i.e. a top-level dir) with no children and no shard is a hand-off to
// that repo's own graph, not a dead end. Returns the repo slug, else null.
export const fleetRepoHandoff = (tree: KgDirtree, path: string): string | null => {
  const dir = tree.dirs.find(d => d.path === path);
  if (!dir || dir.shard || !dir.parent) return null;
  const org = tree.dirs.find(d => d.path === dir.parent);
  if (!org || org.parent !== '') return null;
  if (tree.dirs.some(d => d.parent === path)) return null;
  return path.split('/').length === 2 ? path : null;
};

// An older fleet build drilled below repo rows (?path=org/repo/sub/...). That path no longer
// exists in the tree; when its org/repo prefix is a repo row, hand off with the rest as the
// path inside the repo's own graph. Returns null for any path the tree still has.
export const fleetStaleHandoff = (
  tree: KgDirtree,
  path: string,
): { slug: string; tail: string } | null => {
  if (!path || tree.dirs.some(d => d.path === path)) return null;
  const parts = path.split('/');
  if (parts.length < 3) return null;
  const slug = fleetRepoHandoff(tree, parts.slice(0, 2).join('/'));
  return slug ? { slug, tail: parts.slice(2).join('/') } : null;
};
